/**
 * MODULE: domain/pages/frontend/components/PageCommentNotificationPrefs.tsx
 * PURPOSE: Toggle @mention notification preference for workspace pages
 * RECENT: 2026-05-24 [2.2.1184] — Flowbite Label uses children (not deprecated value prop) for typecheck
 */

import { Label, Spinner, ToggleSwitch } from 'flowbite-react';
import {
  usePageCommentNotificationPrefs,
  useUpdatePageCommentNotificationPrefs,
} from '../hooks/usePages';

type Props = {
  /** Inline on page comments panel vs full-width Settings → Notifications card body */
  variant?: 'inline' | 'settings';
  /** Inside collapsible details — omit outer panel chrome */
  nested?: boolean;
};

export function PageCommentNotificationPrefs({ variant = 'inline', nested = false }: Props) {
  const { data, isLoading, isError } = usePageCommentNotificationPrefs();
  const updatePrefs = useUpdatePageCommentNotificationPrefs();

  const enabled = data?.mention_notifications_enabled !== false;
  const isSettings = variant === 'settings';
  const testIdPrefix = isSettings
    ? 'pages-settings-mention-notification'
    : 'pages-comment-notification';

  return (
    <div
      className={
        nested
          ? undefined
          : isSettings
            ? undefined
            : 'mb-4 rounded-lg border border-gray-100 bg-gray-50/80 px-3 py-3 dark:border-gray-800 dark:bg-gray-900/30'
      }
      data-testid={`${testIdPrefix}-prefs`}
    >
      {isLoading ? (
        <div className={`flex items-center gap-2 text-gray-500 ${isSettings ? 'text-sm' : 'text-xs'}`}>
          <Spinner size="sm" aria-label="Loading notification preferences" />
          Loading notification settings…
        </div>
      ) : isError ? (
        <p className={`text-red-600 ${isSettings ? 'text-sm' : 'text-xs'}`}>
          Could not load notification settings.
        </p>
      ) : (
        <div className="flex items-start justify-between gap-3">
          <div>
            <Label
              htmlFor={`${testIdPrefix}-toggle`}
              className={
                isSettings
                  ? 'font-medium text-gray-900 dark:text-white'
                  : 'text-xs font-medium text-gray-800 dark:text-gray-200'
              }
            >
              Page @mention notifications
            </Label>
            <p
              className={
                isSettings
                  ? 'mt-1 text-sm text-gray-500 dark:text-gray-400'
                  : 'mt-0.5 text-[11px] text-gray-500 dark:text-gray-400'
              }
            >
              Get in-app alerts when someone @mentions you on a workspace page.
            </p>
          </div>
          <ToggleSwitch
            id={`${testIdPrefix}-toggle`}
            checked={enabled}
            disabled={updatePrefs.isPending}
            onChange={(checked) =>
              updatePrefs.mutate({ mention_notifications_enabled: checked })
            }
            data-testid={`${testIdPrefix}-toggle`}
          />
        </div>
      )}
    </div>
  );
}
